// React imports
import { useEffect } from 'react';

// Redux imports
import { useDispatch, useSelector } from 'react-redux';

// Chakra UI components
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Center,
  Spinner,
  useColorModeValue,
  Tooltip,
  Flex,
} from '@chakra-ui/react';

// Custom hooks and utilities
import useToastNotif from 'hooks/useToastNotif';
import { ReducerStatus } from 'utils/constants';
import sessionColumns from 'utils/columns/reports/sessionColumns';

// Redux actions
import { getSubQuestProgressionSessionsAction } from 'store/modules/subQuestReport/subQuest-report-action';
import { subQuestReportActions } from 'store/modules/subQuestReport/subQuest-report-slice';

// Third-party components
import DataTable from 'react-data-table-component';
import { TOAST_ERROR } from 'utils/constants/toast';
import { CloseIcon, EditIcon } from '@chakra-ui/icons';
import { QUIZ_ID } from 'utils/constants/subQuestType';

export const SessionButtons = () => {
  const dispatch = useDispatch();

  const getProgressionSessions = useSelector(
    (state) => state.subQuestReport.getProgressionSessions
  );

  const handleEditSession = (sessionId) => {
    dispatch(
      subQuestReportActions.setUpdateQuizSession({
        subQuestId: getProgressionSessions.subQuestId,
        progressionId: getProgressionSessions.progressionId,
        sessionId,
        modal: true,
      })
    );
  };

  const handleDeleteSession = (sessionId) => {
    dispatch(subQuestReportActions.toggleDeleteProgressionSessionModal(true));
    dispatch(
      subQuestReportActions.setDeleteProgressionSessionValue({
        subQuestId: getProgressionSessions.subQuestId,
        progressionId: getProgressionSessions.progressionId,
        sessionId,
      })
    );
  };

  // Return column configuration for the button
  return {
    name: 'Action',
    sortable: false,
    cell: (row) => (
      <Flex gap={2}>
        {row.typeId === QUIZ_ID && (
          <Tooltip label="Edit Session" fontSize="md">
            <Button
              colorScheme="blue"
              size="xs"
              onClick={() => handleEditSession(row.id)}
            >
              <EditIcon />
            </Button>
          </Tooltip>
        )}

        <Tooltip label="Remove Session" fontSize="md">
          <Button
            colorScheme="gray"
            size="xs"
            onClick={() => handleDeleteSession(row.id)}
          >
            <CloseIcon />
          </Button>
        </Tooltip>
      </Flex>
    ),
  };
};

function ProgressionSessionMd() {
  const dispatch = useDispatch();

  // Custom hook for generating toast notifications
  const { generateToastNotif } = useToastNotif();

  // Redux selectors
  const getProgressionSessions = useSelector(
    (state) => state.subQuestReport.getProgressionSessions
  );
  const sessions = useSelector(
    (state) => state.subQuestReport.progressionSessions
  );

  // Toggles the modal's open/close state
  const handleToggleProgressionSessionsModal = (isOpen) => {
    dispatch(subQuestReportActions.toggleProgressionSessionsModal(isOpen));
  };

  // Determines the table theme based on the current color mode
  const tableTheme = useColorModeValue('default', 'dark');

  // Fetch progression sessions when subQuestId and progressionId are available
  useEffect(() => {
    if (
      getProgressionSessions.subQuestId &&
      getProgressionSessions.progressionId
    ) {
      dispatch(
        getSubQuestProgressionSessionsAction({
          subQuestId: getProgressionSessions.subQuestId,
          progressionId: getProgressionSessions.progressionId,
        })
      );
    }
  }, [
    dispatch,
    getProgressionSessions.subQuestId,
    getProgressionSessions.progressionId,
  ]);

  // Show error toast notification if fetching progression sessions fails
  useEffect(() => {
    if (getProgressionSessions.status === ReducerStatus.FAILED) {
      generateToastNotif(
        TOAST_ERROR,
        getProgressionSessions.error?.data?.status || '404',
        getProgressionSessions.error?.data?.title || 'Not Found',
        true,
        false
      );

      dispatch(subQuestReportActions.toggleProgressionSessionsModal(false));
    }
  }, [
    getProgressionSessions.status,
    getProgressionSessions.error,
    generateToastNotif,
    dispatch,
  ]);

  return (
    <Modal
      isOpen={getProgressionSessions.modal}
      onClose={() => handleToggleProgressionSessionsModal(false)}
      size="lg"
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Sessions</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {/* DataTable to display progression sessions */}
          <DataTable
            theme={tableTheme}
            progressPending={
              getProgressionSessions.status === ReducerStatus.LOADING
            }
            columns={[...sessionColumns, SessionButtons()]}
            data={sessions}
            progressComponent={
              <Center my={10}>
                <Spinner
                  thickness="4px"
                  speed="0.65s"
                  emptyColor="gray.200"
                  color="blue.500"
                  size="xl"
                />
              </Center>
            }
            pagination
          />
        </ModalBody>

        <ModalFooter>
          <Button
            colorScheme="blue"
            mr={3}
            onClick={() => handleToggleProgressionSessionsModal(false)}
          >
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default ProgressionSessionMd;
